const ANCHOR_LENGTH = 80;
const MIN_ANCHOR_LENGTH = 12;

export const CITATION_OVERLAY_COLOR = "rgba(255, 235, 59, 0.2)";

// Sentinel inserted between body lines when joining for the end anchor. Survives all
// normalization passes (no replacement targets it) and gets converted to a flexible regex
// pattern in `escapeFlexibleNeedle`. Using a control character keeps the chance of collision
// with real text effectively zero.
const PARAGRAPH_BOUNDARY = "\x01";

export interface CitationAnchors {
    start: string;
    // Omitted when the body has no usable tail past the start anchor (very short chunks).
    // Matchers fall back to highlighting only the start match when end is missing or unfindable.
    end?: string;
    // A table row. The overlay hugs the matched text instead of banding across the line, because the
    // cells beside it on the same line belong to another column, or another table, not to the citation.
    exact?: true;
}

// Every region of the rendered document a citation highlights, each as a pair of short text anchors
// the viewers find and draw one rectangular overlay between — region-pointer semantics. A prose quote
// is one region: its first sentence to its last ~80 chars, covering whatever sits between (page
// numbers, stripped headings, etc.), the accepted trade-off for one clean rectangle rather than patchy
// per-sentence highlights.
//
// A quote that is nothing but a table is one region per row instead. Side-by-side tables read
// interleaved in a pdf text layer, so a single region from the first row to the last would band
// across the neighbouring table as well; a row on its own is contiguous wherever it is rendered.
// Prose anchors are proven against every viewer while a row depends on the rendering reading its
// cells in order, so a quote with any prose keeps anchoring on the prose alone.
export function deriveCitationAnchorSets(chunkContent: string | undefined): Array<CitationAnchors> {
    if (!chunkContent) return [];

    const lines = chunkContent.split("\n").map((line) => line.trim());
    const proseLines = lines.filter((line) => line && !line.startsWith("#") && !line.includes("|") && !/Page\s+\d+/i.test(line));

    if (proseLines.length > 0) {
        const anchors = deriveAnchorsFromBodyLines(proseLines);
        return anchors ? [anchors] : [];
    }

    return tableRowsAsText(lines)
        .map((row) => deriveAnchorsFromBodyLines([row]))
        .filter((anchors): anchors is CitationAnchors => anchors !== undefined)
        .map((anchors) => ({ ...anchors, exact: true as const }));
}

function deriveAnchorsFromBodyLines(lines: Array<string>): CitationAnchors | undefined {
    const bodyLines = lines
        // Strip markdown-only markers that the indexer captured but the rendered DOM doesn't have:
        //   - Leading "N." / "N.N." numbered list markers (auto-generated by docx-preview)
        //   - Leading "- " / "* " / "+ " / "• " etc. bullet markers (same reason)
        //   - Markdown link wrappers "[text](url)" — keep text, drop url
        //   - Inline emphasis markers "**", "*", "~~", and backticks for code spans
        // The trailing `\s+` on the numeric marker is load-bearing: with `\s*`, the regex would
        // greedy-strip "12." out of "12.3 Obligations" (because zero-width whitespace lets it match
        // without consuming the .3), leaving "3 Obligations" which then fails to match.
        // Literal "\" characters are NOT stripped here — some PDFs keep them in the rendered text
        // (we've seen it on Word→PDF exports of contracts) while docx-preview drops them. The
        // `\` → " " replacement in normalizeForSearch handles both renderings symmetrically.
        .map((line) =>
            line
                .replace(/^\d+(?:\.\d+)*\.\s+/, "")
                .replace(/^[-+*•◦‣⁃∙▪▫●○]\s+/, "")
                .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
                .replace(/\*\*/g, "")
                .replace(/\*/g, "")
                .replace(/~~/g, "")
                .replace(/`/g, "")
        );

    if (bodyLines.length === 0) return undefined;

    const firstLine = bodyLines[0];
    const terminatorIndex = findFirstSentenceTerminator(firstLine);
    const start =
        terminatorIndex >= MIN_ANCHOR_LENGTH && terminatorIndex <= ANCHOR_LENGTH
            ? firstLine.substring(0, terminatorIndex + 1)
            : firstLine.substring(0, Math.min(ANCHOR_LENGTH, firstLine.length));

    if (start.length < MIN_ANCHOR_LENGTH) return undefined;

    // Lines are joined with `\x01` (a control character that won't appear in real text) so
    // `escapeFlexibleNeedle` can recognize the paragraph boundaries later and emit a flexible
    // pattern there that tolerates an optional numbered or bullet marker. The rendered doc still
    // has those markers between paragraphs, but each line had its leading marker stripped above
    // for the auto-numbered-DOCX case — so we need the regex to match both with and without.
    const bodyText = bodyLines.join(PARAGRAPH_BOUNDARY);

    // The end anchor must not overlap the start anchor — the matching code (`computeCitationPageRoles`
    // and `findAnchorRangeInTextNodes`) looks for the end needle *after* the start match consumes its
    // text, so any overlap silently fails. For short bodies (≤ ~2×ANCHOR_LENGTH), the natural "last 80
    // chars" overlap the "first 80 chars"; use the tail-after-start as the end anchor instead.
    // When the body equals the start anchor exactly, no tail exists — omit `end` entirely so matchers
    // fall back to highlighting just the start sentence.
    const tail = bodyText.substring(start.length);
    if (tail.length === 0) return { start };
    const end = tail.length > ANCHOR_LENGTH ? tail.substring(tail.length - ANCHOR_LENGTH) : tail;

    return { start, end };
}

// A markdown table row becomes its cells joined by a space, which is what a rendered table reads as when
// its cells are walked in order: the pdf.js text layer and an HTML table both yield the cells as adjacent
// text nodes, and the needle regex lets any whitespace run between them match. Separator rows only exist
// in the markdown, so they are dropped.
function tableRowsAsText(lines: Array<string>): Array<string> {
    return lines
        .filter((line) => line.includes("|"))
        .map((line) =>
            line
                .split("|")
                .map((cell) => cell.trim())
                .filter((cell) => cell.length > 0)
                .join(" ")
        )
        .filter((row) => row.length > 0 && !/^[-:\s]+$/.test(row));
}

// Returns the index of the first sentence terminator (`.`, `!`, `?`) in `text`, or -1 if
// none. A `.` between two digits (e.g. "$1.50" or "Section 3.2") is treated as a decimal
// separator, not a terminator — without this guard, a body line starting with a price or
// numeric reference would have its anchor truncated mid-number.
function findFirstSentenceTerminator(text: string): number {
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === "!" || ch === "?") return i;
        if (ch !== ".") continue;
        const prev = i > 0 ? text[i - 1] : "";
        const next = i + 1 < text.length ? text[i + 1] : "";
        const isDecimal = prev >= "0" && prev <= "9" && next >= "0" && next <= "9";
        if (!isDecimal) return i;
    }
    return -1;
}

// Normalises common typographic drift between the indexer's text and the rendered document
// (pdf.js text layer or docx-preview DOM): smart quotes/dashes flatten to ASCII, NBSP
// variants become regular spaces, bullet glyphs become hyphens, ellipsis becomes a period,
// and literal backslashes (markdown hard-line-break artefacts that some renderers preserve
// and others drop) become spaces — symmetric normalization on both anchor and rendered text
// means they align regardless of which side has the `\`. Every replacement preserves character
// count (1→1) so byte offsets remain aligned.
export function normalizeForSearch(text: string): string {
    return text
        .replace(/[“”„«»]/g, '"')
        .replace(/[‘’‚]/g, "'")
        .replace(/[–—]/g, "-")
        .replace(/[\u00A0\u2007\u202F]/g, " ")
        .replace(/[•◦‣⁃∙▪▫●○]/g, "-")
        .replace(/…/g, ".")
        .replace(/\\/g, " ");
}

// Escapes regex metacharacters and turns whitespace runs into `\s*` so the pattern matches
// even when the rendered doc has slightly different spacing (missing space after colon, etc.).
// The PARAGRAPH_BOUNDARY marker placed by `deriveCitationAnchors` at line joins is replaced by
// a pattern that tolerates an optional numbered or bullet marker — the chunk-content strip
// removed those from each line (so the joined body text reads "...negotiated.\x01Payment..."),
// but in renderings where the marker is part of the actual text (PDF, manually-numbered DOCX,
// or bullets that pdf.js extracts as glyphs) the rendered text reads "...negotiated.5. Payment..."
// or "...shall:- Process...". The same anchor needs to match both.
function escapeFlexibleNeedle(text: string): string {
    const normalized = normalizeForSearch(text);
    const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const withFlexibleBoundaries = escaped.split(PARAGRAPH_BOUNDARY).join("\\s*(?:\\d+(?:\\.\\d+)*\\.\\s+|-\\s*)?\\s*");
    return withFlexibleBoundaries.replace(/\s+/g, "\\s*");
}

export function buildSingleNeedleRegex(searchText: string): RegExp {
    return new RegExp(escapeFlexibleNeedle(searchText), "gi");
}

export interface AnchorRangePosition {
    startNode: Text;
    startOffset: number;
    endNode: Text;
    endOffset: number;
}

interface NodeEntry {
    node: Text;
    flatStart: number;
    flatEnd: number;
}

function walkTextNodes(root: HTMLElement): { entries: NodeEntry[]; flatText: string } {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const entries: NodeEntry[] = [];
    let flatText = "";
    let textNode = walker.nextNode() as Text | null;
    while (textNode) {
        const value = textNode.nodeValue ?? "";
        entries.push({ node: textNode, flatStart: flatText.length, flatEnd: flatText.length + value.length });
        flatText += value;
        textNode = walker.nextNode() as Text | null;
    }
    return { entries, flatText };
}

function flatOffsetToNode(entries: NodeEntry[], flatOffset: number, prefer: "start" | "end"): { node: Text; offset: number } {
    // For "start", we want the entry where flatOffset falls inside [flatStart, flatEnd).
    // For "end", we want the entry where flatOffset falls inside (flatStart, flatEnd] —
    // a value exactly at a boundary belongs to the preceding entry rather than the next.
    const entry =
        prefer === "start"
            ? (entries.find((e) => flatOffset >= e.flatStart && flatOffset < e.flatEnd) ?? entries[entries.length - 1])
            : (entries.find((e) => flatOffset > e.flatStart && flatOffset <= e.flatEnd) ?? entries[0]);
    return { node: entry.node, offset: Math.max(0, Math.min(flatOffset - entry.flatStart, entry.flatEnd - entry.flatStart)) };
}

// Walks every text node under `root` in document order, builds a flat normalised string of all
// text content, and locates where the start and end anchors fall within that flat string.
// Returns a DOM Range-compatible (node, offset) pair for each end — null when the start anchor
// can't be found. When the end anchor is missing or unfindable, the range collapses to the start
// match alone (one-sentence fallback), so the user still sees a highlight at the citation's head.
export function findAnchorRangeInTextNodes(root: HTMLElement, anchors: CitationAnchors): AnchorRangePosition | null {
    const { entries, flatText } = walkTextNodes(root);
    if (entries.length === 0) return null;

    const normalized = normalizeForSearch(flatText);

    const startMatch = buildSingleNeedleRegex(anchors.start).exec(normalized);
    if (!startMatch) return null;
    const startFlat = startMatch.index;
    const afterStart = startFlat + startMatch[0].length;

    const endFlat = findEndFlat(normalized, afterStart, anchors.end) ?? afterStart;

    const start = flatOffsetToNode(entries, startFlat, "start");
    const end = flatOffsetToNode(entries, endFlat, "end");
    return {
        startNode: start.node,
        startOffset: start.offset,
        endNode: end.node,
        endOffset: end.offset,
    };
}

// Locates the end anchor in the flat text past the start match. Returns null when the anchor
// is missing or unfindable — callers fall back to the post-start position (one-sentence highlight).
function findEndFlat(normalized: string, afterStart: number, endAnchor: string | undefined): number | null {
    if (!endAnchor) return null;
    const tailMatch = buildSingleNeedleRegex(endAnchor).exec(normalized.substring(afterStart));
    if (!tailMatch) return null;
    return afterStart + tailMatch.index + tailMatch[0].length;
}

// The rectangle of each text-node slice between the two ends of `position`, one per slice, so a highlight
// drawn from them covers the matched text and nothing beside it on the same line.
export function getMatchedSliceRects(root: HTMLElement, position: AnchorRangePosition): Array<DOMRect> {
    const { entries } = walkTextNodes(root);
    const startIndex = entries.findIndex((entry) => entry.node === position.startNode);
    const endIndex = entries.findIndex((entry) => entry.node === position.endNode);
    if (startIndex < 0 || endIndex < 0 || endIndex < startIndex) {
        return [];
    }

    const rects: Array<DOMRect> = [];
    for (let i = startIndex; i <= endIndex; i++) {
        const node = entries[i].node;
        const localStart = i === startIndex ? position.startOffset : 0;
        const localEnd = i === endIndex ? position.endOffset : (node.nodeValue?.length ?? 0);
        if (localStart >= localEnd) {
            continue;
        }
        const range = document.createRange();
        range.setStart(node, localStart);
        range.setEnd(node, localEnd);
        const rect = range.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            rects.push(rect);
        }
    }
    return rects;
}

// One bounding rectangle extended halfway into the container's side margins: a citation that starts or
// ends mid-line gets a clean horizontal band, but the highlight doesn't run all the way to the page
// edges. Covering some non-cited text on the start/end lines is an accepted trade-off for the cleaner look.
export function getBandRects(container: HTMLElement, position: AnchorRangePosition): Array<DOMRect> {
    const range = document.createRange();
    range.setStart(position.startNode, position.startOffset);
    range.setEnd(position.endNode, position.endOffset);
    const bounding = range.getBoundingClientRect();
    if (bounding.width <= 0 || bounding.height <= 0) {
        return [];
    }
    const containerRect = container.getBoundingClientRect();
    const leftEdge = (bounding.left + containerRect.left) / 2;
    const rightEdge = (bounding.right + containerRect.right) / 2;
    return [new DOMRect(leftEdge, bounding.top, rightEdge - leftEdge, bounding.height)];
}

// Wraps the text between the two ends of `position` in `<mark>` elements, one per text-node slice, each
// tagged with `markAttribute` so the caller can find and remove its own marks later. Adjacent marks
// render as one continuous highlight even across element boundaries.
//
// Slices are wrapped in reverse document order: surroundContents() splits the text node it wraps, which
// would invalidate the node references of every slice after it.
export function wrapRangeInMarks(root: HTMLElement, position: AnchorRangePosition, markAttribute: string): boolean {
    const { entries } = walkTextNodes(root);
    const startIndex = entries.findIndex((entry) => entry.node === position.startNode);
    const endIndex = entries.findIndex((entry) => entry.node === position.endNode);
    if (startIndex < 0 || endIndex < 0 || endIndex < startIndex) {
        return false;
    }

    let wrappedAny = false;
    for (let i = endIndex; i >= startIndex; i--) {
        const node = entries[i].node;
        const localStart = i === startIndex ? position.startOffset : 0;
        const localEnd = i === endIndex ? position.endOffset : (node.nodeValue?.length ?? 0);
        if (localStart >= localEnd) {
            continue;
        }
        try {
            const range = document.createRange();
            range.setStart(node, localStart);
            range.setEnd(node, localEnd);
            const mark = document.createElement("mark");
            mark.setAttribute(markAttribute, "true");
            range.surroundContents(mark);
            wrappedAny = true;
        } catch {
            // surroundContents only fails when a range crosses element boundaries — every range here has
            // both ends in the same text node, so this is only defensive against unexpected mutations.
        }
    }
    return wrappedAny;
}

// Unwraps every `<mark>` carrying `markAttribute`, restoring the original DOM. The `parent.normalize()`
// call after each unwrap is load-bearing: when surroundContents() inserted the mark it split the original
// text node into three siblings (prefix, marked, suffix). Removing the mark alone would leave prefix and
// suffix as separate adjacent text nodes, and the next TreeWalker pass treats those as independent nodes,
// so a match crossing the old split point would never be found again. normalize() merges them back.
export function unwrapMarks(root: HTMLElement, markAttribute: string) {
    root.querySelectorAll<HTMLElement>(`mark[${markAttribute}]`).forEach((mark) => {
        const parent = mark.parentNode;
        if (!parent) return;
        while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
        parent.removeChild(mark);
        if (parent instanceof HTMLElement) parent.normalize();
    });
}

// Role a single page plays inside a citation that may span multiple pages. Computed at the
// document level by `computeCitationPageRoles`; consumed per-page by `buildCitationRangeInDom`
// to decide how much of the page's text belongs to the highlight.
export type CitationPageRole = "single" | "start" | "middle" | "end";

// Finds which page contains the citation's start anchor and which contains the end anchor,
// and assigns every page in the resulting span a role. The middle pages get role 'middle'
// even though neither anchor lives there — they're entirely within the cited region and need
// to be fully highlighted. Only used for page-level routing; the per-page DOM walk done by
// `buildCitationRangeInDom` is what actually finds the anchor positions when painting.
export function computeCitationPageRoles(anchors: CitationAnchors, pageTexts: Map<number, string>): Map<number, CitationPageRole> {
    if (pageTexts.size === 0) return new Map();

    interface PageBound {
        pageNum: number;
        flatStart: number;
        flatEnd: number;
    }
    const pageBounds: PageBound[] = [];
    let fullText = "";
    const sortedPages = [...pageTexts.keys()].sort((a, b) => a - b);
    for (const pageNum of sortedPages) {
        const text = pageTexts.get(pageNum)!;
        if (fullText.length > 0) fullText += " ";
        const start = fullText.length;
        fullText += text;
        pageBounds.push({ pageNum, flatStart: start, flatEnd: fullText.length });
    }

    const normalized = normalizeForSearch(fullText);
    const startMatch = buildSingleNeedleRegex(anchors.start).exec(normalized);
    if (!startMatch) return new Map();
    const startFlat = startMatch.index;
    const afterStart = startFlat + startMatch[0].length;
    // When end is missing or unfindable, collapse to the start match — the citation becomes a
    // single-page highlight of the start sentence only.
    const endFlat = findEndFlat(normalized, afterStart, anchors.end) ?? afterStart;

    const startPage = pageBounds.find((p) => startFlat >= p.flatStart && startFlat < p.flatEnd);
    const endPage = pageBounds.find((p) => endFlat > p.flatStart && endFlat <= p.flatEnd);
    if (!startPage || !endPage) return new Map();

    const result = new Map<number, CitationPageRole>();
    if (startPage.pageNum === endPage.pageNum) {
        result.set(startPage.pageNum, "single");
        return result;
    }
    result.set(startPage.pageNum, "start");
    result.set(endPage.pageNum, "end");
    for (const p of pageBounds) {
        if (p.pageNum > startPage.pageNum && p.pageNum < endPage.pageNum) {
            result.set(p.pageNum, "middle");
        }
    }
    return result;
}

// Builds a DOM Range against the *rendered* text under `root` — the actual text nodes the
// user sees, not an abstract item list. The role selects how much of the page to cover:
//
//   single: find both anchors here; range = [start anchor → end anchor]
//   start:  find start anchor here; range = [start anchor → last text node]
//   end:    find end anchor here; range = [first text node → end anchor]
//   middle: no anchor lives here; range = [first text node → last text node]
//
// Returns null when the role expects an anchor and the anchor isn't findable, or when the
// root has no text content.
export function buildCitationRangeInDom(root: HTMLElement, anchors: CitationAnchors, role: CitationPageRole): AnchorRangePosition | null {
    const { entries, flatText } = walkTextNodes(root);
    if (entries.length === 0) return null;
    const normalized = normalizeForSearch(flatText);

    let startFlat: number;
    let endFlat: number;

    if (role === "middle") {
        startFlat = 0;
        endFlat = flatText.length;
    } else if (role === "single") {
        const startMatch = buildSingleNeedleRegex(anchors.start).exec(normalized);
        if (!startMatch) return null;
        startFlat = startMatch.index;
        const afterStart = startFlat + startMatch[0].length;
        // End missing or unfindable on this page → highlight only the start match (one sentence).
        endFlat = findEndFlat(normalized, afterStart, anchors.end) ?? afterStart;
    } else if (role === "start") {
        const startMatch = buildSingleNeedleRegex(anchors.start).exec(normalized);
        if (!startMatch) return null;
        startFlat = startMatch.index;
        endFlat = flatText.length;
    } else {
        // "end" — `computeCitationPageRoles` only assigns this role when it located the end anchor
        // in the cross-page flat text, so `anchors.end` is guaranteed to be defined here.
        startFlat = 0;
        if (!anchors.end) return null;
        const endMatch = buildSingleNeedleRegex(anchors.end).exec(normalized);
        if (!endMatch) return null;
        endFlat = endMatch.index + endMatch[0].length;
    }

    const start = flatOffsetToNode(entries, startFlat, "start");
    const end = flatOffsetToNode(entries, endFlat, "end");
    return {
        startNode: start.node,
        startOffset: start.offset,
        endNode: end.node,
        endOffset: end.offset,
    };
}
